import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit, Trash2, Play, Clock } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { PremiumFeatureGate } from '../premium/PremiumFeatureGate';
import { useEffectiveDojo } from '@/hooks/useEffectiveDojo';

interface OnDemandTraining {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  belt_level_id: string | null;
  skill_category: string;
  duration_minutes: number | null;
  difficulty_level: string;
  is_active: boolean;
  created_at: string;
  belt_level?: {
    name: string;
    color: string;
  };
}

interface BeltLevel {
  id: string;
  name: string;
  color: string;
}

const OnDemandTrainingManager = () => {
  useUiLanguage();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { effectiveDojoId, shouldFilterByDojo } = useEffectiveDojo();
  const { hasPremiumAccess, loading: premiumLoading } = usePremiumAccess();
  const [trainings, setTrainings] = useState<OnDemandTraining[]>([]);
  const [beltLevels, setBeltLevels] = useState<BeltLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState<OnDemandTraining | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [beltLevelId, setBeltLevelId] = useState<string>('');
  const [skillCategory, setSkillCategory] = useState<string>('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState<string>('beginner');

  useEffect(() => {
    fetchData();
  }, [profile]);

  const fetchData = async () => {
    await Promise.all([fetchOnDemandTrainings(), fetchBeltLevels()]);
  };

  const fetchOnDemandTrainings = async () => {
    try {
      setIsLoading(true);

      let query = supabase
        .from('on_demand_training')
        .select(`
          *,
          belt_level:belt_levels(name, color)
        `)
        .order('created_at', { ascending: false });

      // Filter by dojo if we should (not super_user or in impersonation mode)
      if (shouldFilterByDojo && effectiveDojoId) {
        query = query.eq('dojo_id', effectiveDojoId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setTrainings(data || []);
    } catch (error) {
      console.error('Error fetching on-demand trainings:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to load training videos."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBeltLevels = async () => {
    try {
      let query = supabase
        .from('belt_levels')
        .select('*')
        .order('order_sequence');

      // Filter by dojo if we should (not super_user or in impersonation mode)
      if (shouldFilterByDojo && effectiveDojoId) {
        query = query.eq('dojo_id', effectiveDojoId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setBeltLevels(data || []);
    } catch (error) {
      console.error('Error fetching belt levels:', error);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setVideoUrl('');
    setThumbnailUrl('');
    setBeltLevelId('');
    setSkillCategory('');
    setDurationMinutes('');
    setDifficultyLevel('beginner');
    setEditingTraining(null);
  };

  const handleOpenDialog = (training?: OnDemandTraining) => {
    if (training) {
      setEditingTraining(training);
      setTitle(training.title);
      setDescription(training.description || '');
      setVideoUrl(training.video_url);
      setThumbnailUrl(training.thumbnail_url || '');
      setBeltLevelId(training.belt_level_id || '');
      setSkillCategory(training.skill_category);
      setDurationMinutes(training.duration_minutes?.toString() || '');
      setDifficultyLevel(training.difficulty_level);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const validateVideoUrl = (url: string): boolean => {
    const videoUrlPatterns = [
      /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/,
      /^https?:\/\/(www\.)?vimeo\.com\/\d+/,
      /^https?:\/\/(www\.)?twitch\.tv\//,
      /^https?:\/\/(www\.)?dailymotion\.com\//
    ];
    return videoUrlPatterns.some(pattern => pattern.test(url));
  };

  const extractYouTubeThumbnail = (url: string): string | null => {
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
    const match = url.match(youtubeRegex);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
    return null;
  };

  const handleSave = async () => {
    if (!title.trim() || !videoUrl.trim() || !skillCategory) {
      toast({
        title: uiText("Validation Error"),
        description: uiText("Please fill in all required fields."),
        variant: "destructive"
      });
      return;
    }

    if (!validateVideoUrl(videoUrl.trim())) {
      toast({
        title: uiText("Invalid Video URL"),
        description: uiText("Please enter a valid YouTube, Vimeo, Twitch, or Dailymotion URL."),
        variant: "destructive"
      });
      return;
    }

    if (!effectiveDojoId) {
      toast({
        title: uiText("Error"),
        description: uiText("Unable to determine dojo. Please try logging in again."),
        variant: "destructive"
      });
      return;
    }

    try {
      console.log('Profile data:', profile);
      console.log('User role:', profile?.role);
      console.log('User dojo_id:', profile?.dojo_id);

      // Auto-generate YouTube thumbnail if not provided
      let finalThumbnailUrl = thumbnailUrl.trim() || null;
      if (!finalThumbnailUrl && validateVideoUrl(videoUrl.trim())) {
        const autoThumbnail = extractYouTubeThumbnail(videoUrl.trim());
        if (autoThumbnail) {
          finalThumbnailUrl = autoThumbnail;
        }
      }

      const trainingData = {
        title: title.trim(),
        description: description.trim() || null,
        video_url: videoUrl.trim(),
        thumbnail_url: finalThumbnailUrl,
        belt_level_id: beltLevelId === 'all' ? null : beltLevelId || null,
        skill_category: skillCategory,
        duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
        difficulty_level: difficultyLevel,
        is_active: true,
        dojo_id: effectiveDojoId!,
        created_by: profile.id
      };

      console.log('Training data to insert:', trainingData);

      let error;

      if (editingTraining) {
        const { error: updateError } = await supabase
          .from('on_demand_training')
          .update(trainingData)
          .eq('id', editingTraining.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('on_demand_training')
          .insert(trainingData);
        error = insertError;
      }

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      toast({
        title: uiText("Success"),
        description: uiText("Training video {{value0}} successfully.", { value0: editingTraining ? 'updated' : 'created' })
      });

      setDialogOpen(false);
      resetForm();
      fetchOnDemandTrainings();
    } catch (error) {
      console.error('Error saving training:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to save training video."),
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (trainingId: string) => {
    console.log('🗑️ Delete button clicked for training ID:', trainingId);
    console.log('🎯 Effective Dojo ID:', effectiveDojoId);
    console.log('🔒 Should filter by dojo:', shouldFilterByDojo);

    if (!confirm(uiText("Are you sure you want to delete this training video? This will also remove all student progress for this video."))) {
      console.log('❌ User cancelled deletion');
      return;
    }

    console.log('✅ User confirmed deletion, proceeding...');

    // CRITICAL FIX: Verify we have effective dojo context
    if (!effectiveDojoId) {
      console.error('❌ No effective dojo ID available');
      toast({
        title: uiText("Error"),
        description: uiText("Unable to determine dojo context. Please refresh and try again."),
        variant: "destructive"
      });
      return;
    }

    try {
      console.log('🔍 Step 1: Verifying training belongs to current dojo...');

      // SECURITY CHECK: Verify the training belongs to the effective dojo before deletion
      let verificationQuery = supabase
        .from('on_demand_training')
        .select('id, dojo_id, title')
        .eq('id', trainingId);

      // Add dojo filter if we should filter by dojo (not super_user or in impersonation)
      if (shouldFilterByDojo) {
        verificationQuery = verificationQuery.eq('dojo_id', effectiveDojoId);
      }

      const { data: trainingToDelete, error: verificationError } = await verificationQuery.single();

      if (verificationError || !trainingToDelete) {
        console.error('❌ Training not found or access denied:', verificationError);
        toast({
          title: uiText("Error"),
          description: uiText("Training video not found or you don't have permission to delete it."),
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Training verified:', trainingToDelete);
      console.log('🔍 Step 2: Deleting progress records for training ID:', trainingId);

      // Delete all progress records for this training video
      const { error: progressError, data: deletedProgress } = await supabase
        .from('on_demand_progress')
        .delete()
        .eq('training_id', trainingId)
        .select();

      console.log('📊 Progress deletion result:', { deletedProgress, progressError });

      if (progressError) {
        console.error('❌ Error deleting progress records:', progressError);
        throw new Error('Failed to delete associated progress records: ' + progressError.message);
      }

      console.log('✅ Step 2 complete. Deleted', deletedProgress?.length || 0, 'progress records');
      console.log('🔍 Step 3: Deleting training video with ID:', trainingId);

      // Delete the training video itself with proper tenant filtering
      let trainingDeleteQuery = supabase
        .from('on_demand_training')
        .delete()
        .eq('id', trainingId);

      // CRITICAL FIX: Add dojo filter to prevent cross-tenant deletion
      if (shouldFilterByDojo) {
        trainingDeleteQuery = trainingDeleteQuery.eq('dojo_id', effectiveDojoId);
      }

      const { error: trainingError, data: deletedTraining } = await trainingDeleteQuery.select();

      console.log('🎥 Training deletion result:', { deletedTraining, trainingError });

      if (trainingError) {
        console.error('❌ Error deleting training video:', trainingError);
        throw trainingError;
      }

      // Verify something was actually deleted
      if (!deletedTraining || deletedTraining.length === 0) {
        console.error('❌ No training video was deleted - possible permissions issue');
        throw new Error(uiText("Training video could not be deleted. This may be due to permissions or the video may not exist in your dojo."));
      }

      console.log('✅ Step 3 complete. Training video deleted successfully');

      toast({
        title: uiText("Success"),
        description: uiText("Training video and all associated progress deleted successfully.")
      });

      console.log('🔄 Refreshing training list...');
      fetchOnDemandTrainings();
      console.log('✅ Deletion process completed successfully');

    } catch (error) {
      console.error('💥 DELETION FAILED:', error);
      toast({
        title: uiText("Error"),
        description: error instanceof Error ? error.message : "Failed to delete training video.",
        variant: "destructive"
      });
    }
  };

  const toggleActive = async (training: OnDemandTraining) => {
    // Add tenant validation
    if (!effectiveDojoId) {
      toast({
        title: uiText("Error"),
        description: uiText("Unable to determine dojo context. Please refresh and try again."),
        variant: "destructive"
      });
      return;
    }

    try {
      let updateQuery = supabase
        .from('on_demand_training')
        .update({ is_active: !training.is_active })
        .eq('id', training.id);

      // Add tenant filter for safety
      if (shouldFilterByDojo && effectiveDojoId) {
        updateQuery = updateQuery.eq('dojo_id', effectiveDojoId);
      }

      const { error } = await updateQuery;

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Training video {{value0}}.", { value0: training.is_active ? 'deactivated' : 'activated' })
      });

      fetchOnDemandTrainings();
    } catch (error) {
      console.error('Error toggling active status:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update training video status."),
        variant: "destructive"
      });
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSkillCategoryIcon = (category: string) => {
    switch (category) {
      case 'kata': return '🥋';
      case 'kumite': return '🥊';
      case 'basics': return '👊';
      case 'conditioning': return '💪';
      case 'philosophy': return '🧘';
      default: return '📚';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center space-y-2">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="text-muted-foreground">{uiText("Loading training videos...")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <PremiumFeatureGate
      featureName={uiText("On-Demand Training Management")}
      description={uiText("Create and manage training videos for students to access anytime. This premium feature allows you to build a comprehensive video library for your dojo.")}
    >
      <div className="space-y-4 sm:space-y-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Play className="h-5 w-5 flex-shrink-0" />
                  <span className="break-words">{uiText("On-Demand Training Management")}</span>
                </CardTitle>
                <CardDescription className="text-sm">
                  {uiText("Create and manage training videos for students to access anytime.")}{!hasPremiumAccess && !premiumLoading && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
                      <strong>{uiText("Premium Feature:")}</strong> {' '}{uiText("Students need a Pro plan to access on-demand training content.")}</div>
                  )}
                </CardDescription>
              </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{uiText("Add Training Video")}</span>
                </Button>
              </DialogTrigger>

              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto mx-4">
                <DialogHeader>
                  <DialogTitle className="text-lg sm:text-xl">
                    {editingTraining ? uiText("Edit Training Video") : uiText("Add New Training Video")}
                  </DialogTitle>
                  <DialogDescription>
                    {uiText("Upload training content for students to access on-demand.")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="title">{uiText("Title *")}</Label>
                      <Input
                        id="title"
                        placeholder={uiText("e.g., Basic Kata Tutorial")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>

                    <div>
                      <Label htmlFor="duration">{uiText("Duration (minutes)")}</Label>
                      <Input
                        id="duration"
                        type="number"
                        placeholder={uiText('e.g., 15')}
                        value={durationMinutes}
                        onChange={(e) => setDurationMinutes(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">{uiText("Description")}</Label>
                    <Textarea
                      id="description"
                      placeholder={uiText("Describe what students will learn...")}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="videoUrl">{uiText("Video URL *")}</Label>
                    <Input
                      id="videoUrl"
                      placeholder={uiText('https://youtube.com/watch?v=... or https://vimeo.com/...')}
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                    />
                  </div>

                  <div>
                    <Label htmlFor="thumbnailUrl">{uiText("Thumbnail URL")}</Label>
                    <Input
                      id="thumbnailUrl"
                      placeholder="https://example.com/thumbnail.jpg"
                      value={thumbnailUrl}
                      onChange={(e) => setThumbnailUrl(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="skillCategory">{uiText("Skill Category *")}</Label>
                      <Select value={skillCategory} onValueChange={setSkillCategory}>
                        <SelectTrigger>
                          <SelectValue placeholder={uiText("Select category")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kata">{uiText("Kata")}</SelectItem>
                          <SelectItem value="kumite">{uiText("Kumite")}</SelectItem>
                          <SelectItem value="basics">{uiText("Basics")}</SelectItem>
                          <SelectItem value="conditioning">{uiText("Conditioning")}</SelectItem>
                          <SelectItem value="philosophy">{uiText("Philosophy")}</SelectItem>
                          <SelectItem value="other">{uiText("Other")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="difficultyLevel">{uiText("Difficulty Level")}</Label>
                      <Select value={difficultyLevel} onValueChange={setDifficultyLevel}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">{uiText("Beginner")}</SelectItem>
                          <SelectItem value="intermediate">{uiText("Intermediate")}</SelectItem>
                          <SelectItem value="advanced">{uiText("Advanced")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="beltLevel">{uiText("Target Belt Level")}</Label>
                    <Select value={beltLevelId} onValueChange={setBeltLevelId}>
                      <SelectTrigger>
                        <SelectValue placeholder={uiText("Select belt level (optional)")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{uiText("All Levels")}</SelectItem>
                        {beltLevels.map((belt) => (
                          <SelectItem key={belt.id} value={belt.id}>
                            {belt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    {uiText("Cancel")}</Button>
                  <Button onClick={handleSave}>
                    {editingTraining ? uiText("Update") : uiText("Create")} {' '}{uiText("Training Video")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {trainings.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Play className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{uiText("No Training Videos")}</h3>
            <p className="text-muted-foreground text-center mb-4">
              {uiText("Start building your on-demand training library by adding your first video.")}</p>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              {uiText("Add First Training Video")}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trainings.map((training) => (
            <Card key={training.id} className={`overflow-hidden ${!training.is_active ? 'opacity-60' : ''}`}>
              <div className="relative">
                {training.thumbnail_url ? (
                  <img
                    src={training.thumbnail_url}
                    alt={training.title}
                    className="w-full h-32 object-cover"
                  />
                ) : (
                  <div className="w-full h-32 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                    <span className="text-4xl">{getSkillCategoryIcon(training.skill_category)}</span>
                  </div>
                )}

                {!training.is_active && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Badge variant="secondary">{uiText("Inactive")}</Badge>
                  </div>
                )}
              </div>

              <CardContent className="p-4">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold line-clamp-2">{training.title}</h3>
                    {training.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {training.description}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className={getDifficultyColor(training.difficulty_level)}>
                      {training.difficulty_level}
                    </Badge>

                    <Badge variant="outline">
                      {training.skill_category}
                    </Badge>

                    {training.belt_level && (
                      <Badge variant="outline" style={{ borderColor: training.belt_level.color }}>
                        {training.belt_level.name}
                      </Badge>
                    )}
                  </div>

                  {training.duration_minutes && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {training.duration_minutes} {' '}{uiText("minutes")}</div>
                  )}

                   <div className="flex flex-col sm:flex-row gap-2">
                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => handleOpenDialog(training)}
                       className="w-full sm:flex-1"
                     >
                       <Edit className="h-3 w-3 mr-1 flex-shrink-0" />
                       <span>{uiText("Edit")}</span>
                     </Button>

                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => toggleActive(training)}
                       className="w-full sm:w-auto"
                     >
                       <span className="truncate">{training.is_active ? uiText("Deactivate") : uiText("Activate")}</span>
                     </Button>

                     <Button
                       variant="outline"
                       size="sm"
                       onClick={() => handleDelete(training.id)}
                       className="w-full sm:w-auto text-red-600 hover:text-red-700"
                     >
                       <Trash2 className="h-3 w-3 mr-1 flex-shrink-0" />
                       <span>{uiText("Delete")}</span>
                     </Button>
                   </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
    </PremiumFeatureGate>
  );
};

export default OnDemandTrainingManager;
